<template>
  <v-expansion-panels accordion>
    <v-expansion-panel class="elevation-2">
      <v-expansion-panel-header class="px-4 panel-header">
        <div class="d-flex align-center">
          <v-tooltip top>
            <template #activator="{ on, attrs }">
              <v-btn @click.stop="fetchAditionals" :loading="isLoading" v-on="on" v-bind="attrs" small color="primary">
                <v-icon>mdi-reload</v-icon>
              </v-btn>
            </template>
            Recarregar listagem
          </v-tooltip>
          <v-icon size="20" class="mx-2"> mdi-information-outline </v-icon>
          <h4>{{ expandTitle }}</h4>
        </div>
        <template v-slot:actions>
          <v-icon color="black"> mdi-chevron-down </v-icon>
        </template>
      </v-expansion-panel-header>
      <v-expansion-panel-content>
        <div class="mx-4 mt-4 d-flex flex-column">
          <v-data-table
            v-model="selectedAditionals"
            :items="filteredAditionalRelated"
            :headers="aditionalsHeaders"
            :height="unselectedAditionalsTableHeight"
            :items-per-page="filteredAditionalRelated.length"
            fixed-header
            @change="$emit('input', value)"
            no-data-text="Sem adicionais para vincular"
            item-key="id"
            :loading="isLoading"
            loading-text="Carregando..."
            :show-select="filteredAditionalRelated.length > 0"
            hide-default-footer
          >
            <template v-slot:[`item.isDiscount`]="{ item }">
              {{ getAditionalIsDiscount(item.isDiscount) }}
            </template>
            <template v-slot:[`item.price`]="{ item }">
              {{ transformPrice(item.price) }}
            </template>
            <template v-slot:[`item.additionalDate`]="{ item }">
              {{ formatDate(item.additionalDate) }}
            </template>
            <template v-slot:[`item.accessKeyCte`]="{ item }">
              <TruncateDataWithTooltip :data="item.accessKeyCte" :lengthDataToTruncate="10"></TruncateDataWithTooltip>
            </template>
            <template v-slot:[`item.complementaryAccessKeyCte`]="{ item }">
              <TruncateDataWithTooltip
                :data="item.complementaryAccessKeyCte"
                :lengthDataToTruncate="10"
              ></TruncateDataWithTooltip>
            </template>
            <template v-slot:[`item.justification`]="{ item }">
              {{ getAditionalJustification(item.justification) }}
            </template>
            <template v-slot:[`item.observation`]="{ item }">
              <TruncateDataWithTooltip :data="item.observation" :lengthDataToTruncate="10"></TruncateDataWithTooltip>
            </template>
          </v-data-table>
          <div class="my-4 d-flex justify-end">
            <v-btn @click="addRelatedAditionals()" :disabled="selectedAditionals.length == 0" color="success">
              Adicionar na Fatura
            </v-btn>
          </div>
        </div>
      </v-expansion-panel-content>
    </v-expansion-panel>
  </v-expansion-panels>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import maskPrice from '@/utils/maskPrice';
import TruncateDataWithTooltip from '@/components/Table/TruncateDataWithTooltip.vue';

export default {
  props: {
    shipperId: {
      default: null,
      type: String
    },
    value: {
      default: () => [],
      type: Array
    },
    aditionalRelated: {
      default: () => [],
      type: Array
    }
  },

  components: {
    TruncateDataWithTooltip
  },

  data: () => ({
    selectedAditionals: [],
    isLoading: false,
    aditionalRelatedIds: [],
    aditionals: []
  }),

  computed: {
    aditionalsHeaders() {
      return [
        {
          value: 'isDiscount',
          text: 'TIPO',
          sortable: false
        },
        {
          value: 'price',
          text: 'VALOR',
          sortable: false
        },
        {
          value: 'additionalDate',
          text: 'DATA',
          sortable: false
        },
        {
          value: 'accessKeyCte',
          text: 'CHAVE CTE',
          sortable: false
        },
        {
          value: 'complementaryAccessKeyCte',
          text: 'CTE COMPLEMENTAR',
          sortable: false
        },
        {
          value: 'justification',
          text: 'JUSTIFICATIVA',
          sortable: false
        },

        {
          value: 'observation',
          text: 'OBSERVAÇÃO',
          sortable: false
        }
      ];
    },

    filteredAditionalRelated() {
      const aditionalsIds = this.aditionalRelated.map((aditional) => aditional.id);
      return this.aditionals.filter((aditional) => !aditionalsIds.includes(aditional.id));
    },

    unselectedAditionalsTableHeight() {
      const hasMoreThanFiveAditional = this.filteredAditionalRelated.length > 5;
      return hasMoreThanFiveAditional ? '250' : '';
    },

    expandTitle() {
      const hasRelatedAditionals = this.filteredAditionalRelated.length > 0;
      return hasRelatedAditionals ? 'Existem adicionais previamente vinculado' : 'Sem adicionais vinculados';
    },

    ...mapGetters('NfeStore', ['getJustificationsById'])
  },

  async created() {
    await this.fetchAditionals();
  },

  watch: {
    shipperId: {
      async handler(shipperId) {
        if (shipperId !== null) {
          this.fetchAditionals();
        }
      }
    }
  },

  methods: {
    ...mapActions('ShippersReceivableStore', ['getRelatedAditionalsReceivableByShipperId']),

    async fetchAditionals() {
      try {
        this.isLoading = true;

        if (this.shipperId !== null) {
          const aditionals = await this.getRelatedAditionalsReceivableByShipperId(this.shipperId);

          this.aditionals = aditionals;

          this.$emit('input', this.aditionals);
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.isLoading = false;
      }
    },

    addRelatedAditionals() {
      const newAditionals = this.selectedAditionals.map((aditional) => {
        aditional = {
          ...aditional,
          additionalDate: aditional.additionalDate.slice(0, 10)
        };

        const isStringJustification = typeof aditional.justification === 'string';
        const justification = isStringJustification
          ? this.getJustificationsById.find((justification) => justification.name === aditional.justification)
          : aditional.justification;
        return { ...aditional, justification, isRelatedAditional: true };
      });

      this.$emit('clickAddAditionals', newAditionals);
      this.selectedAditionals = [];
    },

    formatDate(date) {
      const [year, month, day] = date.slice(0, 10).split('-');
      return `${day}/${month}/${year}`;
    },

    getAditionalIsDiscount(isDiscount) {
      return isDiscount ? 'Débito' : 'Crédito';
    },

    getAditionalJustification(justification) {
      return typeof justification === 'string' ? justification : justification.name;
    },
    transformPrice(price) {
      return maskPrice(price);
    }
  }
};
</script>

<style lang="scss" scoped>
.panel-header {
  background: $--color-table-offset;
}
.v-expansion-panel-content::v-deep .v-expansion-panel-content__wrap {
  padding: 0 !important;
}
</style>
